import {
  EntityStateDTO,
  SetupTemperatureDTO,
  TemperatureType,
} from 'business/models/Models';
import { RMCheckBoxY } from 'components/customComponents/Controls/RMCheckbox';
import TextInput from 'components/customComponents/Experiment/ImagingSchedule/TextInput';
import React, { memo, useCallback, useContext, useEffect, useState } from 'react';
import Table from './scoreTable';
import './setup.css';
import { getNewId } from 'utils/Global';
import { TempConfigContext } from '../SetupDashboard';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import ExperimentService from 'business/services/ExperimentService';
import { ModalDetails } from 'business/models/CustomModels';
import RMModal from 'components/modal/RMModal';

export const SetupTemperature = memo(() => {
  const { setupTemp, setSetupTemp } = useContext(TempConfigContext);

  const [values, setValues] = useState([]);
  const [isAllChecked, setIsAllChecked] = useState<boolean>(false);
  const [selectionList, setSelectionList] = useState<boolean[]>(
    new Array(setupTemp.length).fill(false),
  );
  const [modalDetails, setModalDetails] = useState<ModalDetails>({
    showModal: false,
    setShowModal: () => {},
    onOk: () => {},
    body: null,
    heading: '',
    isOkOnly: false,
    okButtonText: undefined,
    noButtonText: undefined,
    showButtons: true,
  });
  const [selectedRowIndex, setSelectedRowIndex] = useState<number>(-1);

  const getTableColumns: object[] = [
    {
      Header: (
        <RMCheckBoxY
          isChecked={isAllChecked}
          onChange={() => onClickIsAllChecked(!isAllChecked)}
        />
      ),
      accessor: 'id',
      className: 'check-box-table',
    },
    {
      Header: (<span id="scoresettings-header-temperature">
        Temperature (*C)
      </span>),
      accessor: 'temperature',
    },
    {
      Header: (
      <span id="scoresettings-header-chilled">
        Chilled
      </span>),
      accessor: 'ischilled',
      className: 'chilled',
    },
  ];

  useEffect(() => {
    setupTable();
  }, [setupTemp, selectionList]);

  const handleOnRowClick = (row) => {
    setSelectedRowIndex(row.index);
    setSelectionList((selectionList) =>
      selectionList?.map((_, index) => index === row.index),
    );
  };

  const setupTempTable = useCallback(() => {
    if (values !== null) {
      return (
        <Table
          columns={getTableColumns}
          data={values}
          onRowClick={handleOnRowClick}
          selectedRowIndex={selectedRowIndex}
        />
      );
    }
    return null;
  }, [values, selectedRowIndex]);

  const setupTable = useCallback(() => {
    const tableData = setupTemp.map((temp, index) => {
      selectionList[index] = selectionList[index] ? selectionList[index] : false;
      const entry = {
        id: (
          <RMCheckBoxY
            isChecked={selectionList[index]}
            onChange={() => onClickSelection(!selectionList[index], index)}
          />
        ),
        temperature: (
          <TextInput
            type="text"
            value={temp.temperature}
            id={`setuptemp-table-input-${temp.id}`}
            rowData={temp}
            onChange={onchangeTemperature}
            index={index}
          />
        ),
        ischilled: (
          <RMCheckBoxY
            isChecked={temp.ischilled}
            onChange={() => onClickIsChilled(!temp.ischilled, index)}
          />
        ),
      };
      return entry;
    });
    setValues(tableData);
  }, [setupTemp, selectionList]);

  const onchangeTemperature = useCallback(
    ({ value, index }) => {
      const experimentTemperature = [...setupTemp];
      experimentTemperature[index].temperature = value;
      experimentTemperature[index].entityState = EntityStateDTO.Modified;
      setSetupTemp(experimentTemperature);
    },
    [setupTemp],
  );

  const onClickIsAllChecked = useCallback(
    (value: boolean) => {
      setSelectionList(new Array(selectionList.length).fill(value));
      setIsAllChecked(value);
    },
    [selectionList],
  );

  const onClickIsChilled = useCallback(
    (value: boolean, index: number) => {
      const experimentTemperature = [...setupTemp];
      experimentTemperature[index].ischilled = value;
      experimentTemperature[index].entityState = EntityStateDTO.Modified;
      setSetupTemp(experimentTemperature);
    },
    [setupTemp],
  );

  const onClickSelection = useCallback(
    (value: boolean, index: number) => {
      const tempSelectionList = [...selectionList];
      tempSelectionList[index] = value;
      setSelectionList(tempSelectionList);
      tempSelectionList.some((item) => item === false)
        ? setIsAllChecked(false)
        : setIsAllChecked(true);
    },
    [selectionList],
  );

  const addNewConfig = useCallback(() => {
    const newItem = new SetupTemperatureDTO({
      id: getNewId(),
      temperature: '0',
      ischilled: false,
      entityState: EntityStateDTO.New,
    });
    const newList = setupTemp.concat(newItem);
    const tempSelectionList = [...selectionList];
    setSetupTemp(newList);
    const newSelectionList = tempSelectionList.concat(false);
    setSelectionList(newSelectionList);
  }, [setupTemp, selectionList]);

  const deleteConfig = useCallback(async () => {
    const expService = new ExperimentService();
    const selectedTemperatures = setupTemp.filter(
      (item, index) => selectionList[index] === true,
    );
    const ids: number[] = selectedTemperatures.map((temp) => {
      return temp.id;
    });
    if (ids.length > 0) {
      const isInUse = await expService.isTemperatureInUse(ids, TemperatureType.Setup);
      if (isInUse === true) {
        setModalDetails({
          ...modalDetails,
          isOkOnly: true,
          showModal: true,
          onOk: () => setModalDetails({ ...modalDetails, showModal: false }),
          body: 'You cannot delete this temperature because it is currently in use.',
          heading: 'Warning',
          okButtonText: 'Ok',
        });
        return;
      }
    }
    const newSelectedList = setupTemp.filter(
      (item, index) => selectionList[index] !== true,
    );
    if (newSelectedList.length > 0) {
      setSetupTemp(newSelectedList);
      setSelectionList(new Array(newSelectedList.length).fill(false));
    } else {
      setSetupTemp([]);
      setSelectionList([]);
      setIsAllChecked(false);
    }
  }, [setupTemp, selectionList]);

  return (
    <>
      <div id='setuptemperature-wrapper' className="float-left p-1 pt-1 setup-table-container">
        <div id="setuptemperature-header" className="p-1 flex justify-between">
          <label id="setuptemperature-title-header" className="truncate">Setup Temperature</label>
          <div id="setuptemperature-action-buttons" className="flex justify-between gap-1">
            <div id="setuptemperature-add-button" className="float-left cursor-pointer" onClick={(e) => addNewConfig()}>
              <RMSvgIcon
                id="setuptemperature-icon-add"
                name="Add"
                width={'28px'}
                height={'28px'}
                fill="#474747"
                subfolder="Setup"
              ></RMSvgIcon>
            </div>
            <div id="setuptemperature-remove-button" className="float-left cursor-pointer" onClick={(e) => deleteConfig()}>
              <RMSvgIcon
                id="setuptemperature-icon-remove"
                name="Remove"
                width={'28px'}
                height={'28px'}
                opacity={selectionList.filter((i) => i === true).length ? 1 : 0.5}
                fill="#474747"
                subfolder="Setup"
              ></RMSvgIcon>
            </div>
          </div>
        </div>
        <div id="setuptemperature-table-wrapper" className="setup-table-parent">
          <div id="setuptemperature-table" className="setup-table">{setupTempTable()}</div>
        </div>
      </div>
      <RMModal id="setuptemperature-modal" {...modalDetails} />
    </>
  );
});
